import React from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { Briefcase } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const FONT = { fontFamily: "'Poppins', sans-serif" };

export const jdAutocompleteListboxSx = {
  p: 1,
  maxHeight: 320,
  ...FONT,
  '& .MuiAutocomplete-option': {
    borderRadius: '10px',
    mb: 0.5,
    px: 1,
    py: 0.5,
    ...FONT,
    '&[aria-selected="true"]': {
      backgroundColor: 'rgba(14, 165, 233, 0.10)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(14, 165, 233, 0.08)',
    },
  },
};

export const jdAutocompletePaperProps = {
  elevation: 8,
  sx: {
    borderRadius: '16px',
    mt: 1,
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.12), 0 8px 10px -6px rgba(15, 23, 42, 0.08)',
  },
};

export const BulkScheduleJdOption = ({ option, compact = false, showStatus = true }) => {
  const { t } = useTranslation();
  const status = option.matching_status;
  const isProcessing = status?.toLowerCase() === 'processing';
  const iconSize = compact ? 32 : 40;
  const lucideSize = compact ? 16 : 20;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', py: compact ? 0.5 : 1, width: '100%', gap: compact ? 1.25 : 2, minWidth: 0 }}>
      <Box sx={{
        width: iconSize,
        height: iconSize,
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#0284c7',
        flexShrink: 0,
        background: 'rgba(14, 165, 233, 0.10)',
      }}>
        <Briefcase size={lucideSize} />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          sx={{
            ...FONT,
            fontWeight: 600,
            fontSize: compact ? '0.8125rem' : '0.875rem',
            color: '#0f172a',
            mb: 0.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {option.title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, color: '#64748b', minWidth: 0 }}>
          <Typography sx={{ ...FONT, fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'currentColor', flexShrink: 0 }} />
            {option.department_name || t('common.noDepartment', 'No Department')}
          </Typography>
          {option.work_type && (
            <Typography sx={{ ...FONT, fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'currentColor' }} />
              {option.work_type.replace('_', ' ')}
            </Typography>
          )}
        </Box>
      </Box>
      {showStatus && status && (
        <Box sx={{
          px: 1.25,
          py: 0.35,
          borderRadius: '8px',
          fontSize: '0.65rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.025em',
          flexShrink: 0,
          ...FONT,
          bgcolor: isProcessing ? 'rgba(14, 165, 233, 0.08)' : 'rgba(22, 163, 74, 0.08)',
          color: isProcessing ? '#0369a1' : '#15803d',
          border: '1px solid',
          borderColor: isProcessing ? '#bae6fd' : '#bbf7d0',
        }}>
          {t(`jobDescription.status${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`, status)}
        </Box>
      )}
    </Box>
  );
};

const BulkScheduleJdSelect = ({
  jdOptions = [],
  selectedJd,
  onChange,
  onInputChange,
  searchTerm = '',
  loadingJds = false,
  sx,
}) => {
  const { t } = useTranslation();

  return (
    <Autocomplete
      fullWidth
      options={jdOptions}
      getOptionLabel={(option) => option.title || ''}
      isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
      value={jdOptions.find((option) => String(option.id) === String(selectedJd)) || null}
      onChange={onChange}
      onInputChange={onInputChange}
      loading={loadingJds}
      loadingText={t('bulkSchedule.loadingJds')}
      noOptionsText={searchTerm ? t('bulkSchedule.noJdsFound') : t('bulkSchedule.startTypingSearch')}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <li key={option.id || key} {...rest}>
            <BulkScheduleJdOption option={option} />
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('bulkSchedule.searchJdLabel')}
          variant="outlined"
          placeholder={t('bulkSchedule.searchJdPlaceholder', 'Search and select a job description...')}
          InputLabelProps={{ sx: FONT }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingJds ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: {
              borderRadius: '12px',
              fontFamily: FONT.fontFamily,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e2e8f0',
                borderWidth: '2px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#cbd5e1',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0ea5e9',
              },
            }
          }}
        />
      )}
      ListboxProps={{ sx: jdAutocompleteListboxSx }}
      PaperProps={jdAutocompletePaperProps}
      slotProps={{ popper: { sx: { zIndex: 1400 } } }}
      sx={sx}
    />
  );
};

export default BulkScheduleJdSelect;
